import { motion, AnimatePresence } from 'motion/react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface PrivacyPolicyViewProps {
  open: boolean;
  onClose: () => void;
}

export function PrivacyPolicyView({ open, onClose }: PrivacyPolicyViewProps) {
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-white flex flex-col"
        >
          <div className="flex items-center justify-between p-6 border-b border-stone-100">
            <h1 className="text-xl font-extrabold tracking-tight text-stone-900">Politique de confidentialité</h1>
            <button onClick={onClose} className="p-2 bg-stone-100 rounded-full text-stone-500 hover:text-stone-900 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 max-w-2xl mx-auto">
            <div className="prose prose-stone prose-sm max-w-none space-y-6 text-stone-700 text-sm leading-relaxed pb-24">
              <p className="text-xs text-stone-400">Dernière mise à jour : 11 mars 2026</p>

              <h2 className="text-base font-bold text-stone-900">1. Responsable du traitement</h2>
              <p>Atilum est une application éditée par Atilum SAS. Le responsable du traitement des données personnelles est Atilum SAS.</p>

              <h2 className="text-base font-bold text-stone-900">2. Données collectées</h2>
              <p>Nous collectons les données suivantes :</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Données de compte</strong> : adresse email, mot de passe (hashé), date de création du compte.</li>
                <li><strong>Données de profil</strong> : réponses au questionnaire d'onboarding (âge, genre, habitudes de vie, antécédents médicaux), score de fertilité calculé.</li>
                <li><strong>Données d'abonnement</strong> : identifiant client Paddle, identifiant d'abonnement, date d'expiration de l'abonnement.</li>
              </ul>

              <h2 className="text-base font-bold text-stone-900">3. Finalités du traitement</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>Fourniture du service : calcul du score de fertilité, programme personnalisé, suivi quotidien.</li>
                <li>Gestion de l'abonnement et de la facturation via Paddle.</li>
                <li>Authentification et sécurité du compte via Supabase Auth.</li>
              </ul>

              <h2 className="text-base font-bold text-stone-900">4. Bases légales</h2>
              <p>Le traitement est fondé sur :</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>L'exécution du contrat (fourniture du service).</li>
                <li>Le consentement (données de santé liées au questionnaire).</li>
                <li>L'intérêt légitime (amélioration du service, sécurité).</li>
              </ul>

              <h2 className="text-base font-bold text-stone-900">5. Sous-traitants</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Supabase</strong> (hébergement, base de données, authentification) — serveurs UE.</li>
                <li><strong>Paddle</strong> (facturation, paiement) — conforme PCI-DSS.</li>
              </ul>

              <h2 className="text-base font-bold text-stone-900">6. Durée de conservation</h2>
              <p>Les données sont conservées tant que votre compte est actif. En cas de suppression du compte, toutes les données sont supprimées immédiatement et de manière irréversible.</p>

              <h2 className="text-base font-bold text-stone-900">7. Vos droits (RGPD)</h2>
              <p>Conformément au Règlement Général sur la Protection des Données, vous disposez des droits suivants :</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Droit d'accès</strong> : obtenir une copie de vos données.</li>
                <li><strong>Droit de rectification</strong> : corriger vos données inexactes.</li>
                <li><strong>Droit à l'effacement</strong> : supprimer votre compte et toutes vos données.</li>
                <li><strong>Droit à la portabilité</strong> : recevoir vos données dans un format structuré.</li>
                <li><strong>Droit d'opposition</strong> : vous opposer au traitement de vos données.</li>
              </ul>
              <p>Pour exercer ces droits, contactez-nous à : <strong>contact@atilum.com</strong></p>

              <h2 className="text-base font-bold text-stone-900">8. Sécurité</h2>
              <p>Les mots de passe sont hashés. Les communications sont chiffrées via TLS. L'accès aux données est restreint par des politiques Row Level Security (RLS) dans Supabase.</p>

              <h2 className="text-base font-bold text-stone-900">9. Contact</h2>
              <p>Pour toute question relative à cette politique, contactez-nous à <strong>contact@atilum.com</strong>.</p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
